import React, {FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {isElement} from '../../../../libs/shared/helpers/src/browser/domHelpers';

/*
 * Constants.
 */

const boundaryThreshold = 10; // 10px;

/*
 * Props.
 */

interface DynamicScrollBoundariesProps {
  hasTopBoundary?: boolean;
  hasBottomBoundary?: boolean;
  color: string;
  className?: string;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto;
`;

const StyledTopBoundaryDiv = styled.div`
  grid-row: 1;
  grid-column: 1;
  height: 1px;
`;

const StyledBottomBoundaryDiv = styled(StyledTopBoundaryDiv)`
  grid-row: 3;
`;

const StyledContentDiv = styled.div`
  grid-row: 2;
  grid-column: 1;

  min-height: 0;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
`;

/*
 * Component.
 */

/** Add separators at the top and/or bottom that appear on scroll when needed. */
export const DynamicScrollBoundaries: FC<DynamicScrollBoundariesProps> = (props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const topBoundaryRef = useRef<HTMLDivElement>(null);
  const bottomBoundaryRef = useRef<HTMLDivElement>(null);
  const [scroller, setScroller] = useState<Element | null>(null);

  /*
   * Find our scroller child.
   */

  useLayoutEffect(() => {
    const currentContent = contentRef.current;
    if (!currentContent) {
      return;
    }

    const {firstChild} = currentContent;
    if (scroller === firstChild) {
      return;
    }

    const firstElement = (isElement(firstChild) && firstChild) || null;
    setScroller(firstElement);
  }, [scroller, setScroller]);

  /*
   * Handle scroll events on the scroller.
   */

  // Update the top boundary opacity.
  const onScrollTopBoundary = useCallback(() => {
    const currentTopBoundary = topBoundaryRef.current;
    if (!scroller || !currentTopBoundary) {
      return;
    }

    const {scrollTop} = scroller;
    const newOpacity = Math.min(scrollTop / boundaryThreshold, 1).toString();
    if (currentTopBoundary.style.opacity === newOpacity) {
      return;
    }

    currentTopBoundary.style.opacity = newOpacity;
  }, [scroller]);

  // Update the bottom boundary opacity.
  const onScrollBottomBoundary = useCallback(() => {
    const currentBottomBoundary = bottomBoundaryRef.current;
    if (!scroller || !currentBottomBoundary) {
      return;
    }

    const {scrollTop, scrollHeight, clientHeight} = scroller;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    const newOpacity = Math.min(scrollBottom / boundaryThreshold, 1).toString();
    if (currentBottomBoundary.style.opacity === newOpacity) {
      return;
    }

    currentBottomBoundary.style.opacity = newOpacity;
  }, [scroller]);

  // Combined scroll handler.
  const onScroll = useCallback(() => {
    onScrollTopBoundary();
    onScrollBottomBoundary();
  }, [onScrollTopBoundary, onScrollBottomBoundary]);

  // Register for scroll events on the scroller.
  useEffect(() => {
    if (!scroller) {
      return undefined;
    }

    onScroll();
    scroller.addEventListener('scroll', onScroll);
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [scroller, onScroll]);

  useLayoutEffect(() => {
    const currentContent = contentRef.current;
    if (!currentContent) {
      return;
    }

    if (!scroller) {
      return;
    }

    onScroll();
  });

  /*
   * Render.
   */

  const {hasTopBoundary, hasBottomBoundary, color, className, children} = props;
  const defaultStyle = useMemo(() => ({backgroundColor: color, opacity: 0}), [color]);

  return (
    <StyledWrapperDiv className={className}>
      {hasTopBoundary && <StyledTopBoundaryDiv ref={topBoundaryRef} style={defaultStyle} />}
      <StyledContentDiv ref={contentRef}>{children}</StyledContentDiv>
      {hasBottomBoundary && <StyledBottomBoundaryDiv ref={bottomBoundaryRef} style={defaultStyle} />}
    </StyledWrapperDiv>
  );
};
